// progress.ts — what a user has already seen of each tour.
//
// Stored per (user, tour) so a free user who upgrades still gets the paid tour,
// and two accounts on one browser don't share state. localStorage only: a new
// device repeats the tour once, which is cheaper than a migration and a
// profile endpoint for a feature this small.
//
// Versioning: each step carries `addedIn`. A returning user is only shown steps
// newer than the version they last saw ("what's new"); a replay shows them all.

import type { TourDef, TourStep } from "./types";

/** Pre-versioned key, still written by the e2e specs to suppress the tour. */
export const LEGACY_DONE_KEY = "onboarding_complete";
/** What the legacy key counts as having seen: the original 5-step tour. */
export const LEGACY_SEEN_VERSION = 1;

export interface TourProgress {
  seen: number;
  outcome: "completed" | "dismissed";
  at: string;
}

export function progressKey(userId: string, tourId: string): string {
  return `parcely.tour:${userId}:${tourId}`;
}

export function readProgress(userId: string, tourId: string): TourProgress | null {
  try {
    const raw = localStorage.getItem(progressKey(userId, tourId));
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<TourProgress>;
    if (typeof p.seen !== "number") return null;
    return { seen: p.seen, outcome: p.outcome === "dismissed" ? "dismissed" : "completed", at: String(p.at ?? "") };
  } catch {
    return null;
  }
}

export function writeProgress(userId: string, def: TourDef, outcome: TourProgress["outcome"], now: Date = new Date()): void {
  try {
    localStorage.setItem(progressKey(userId, def.id), JSON.stringify({ seen: def.version, outcome, at: now.toISOString() }));
  } catch {
    /* storage blocked — the tour will simply offer itself again next load */
  }
}

function legacyDone(): boolean {
  try {
    return !!localStorage.getItem(LEGACY_DONE_KEY);
  } catch {
    return false;
  }
}

/** Steps to show on an automatic start. Empty means "don't show the tour". */
export function stepsForAutoStart(def: TourDef, userId: string): TourStep[] {
  const progress = readProgress(userId, def.id);
  const seen = progress ? progress.seen : legacyDone() ? LEGACY_SEEN_VERSION : 0;
  return def.steps.filter((s) => s.addedIn > seen);
}

/** Steps to show on a replay: all of them. */
export function stepsForReplay(def: TourDef): TourStep[] {
  return def.steps;
}
